import type { ReactNode } from "react";
import type { Utility } from "@cs2/contracts/domain";
import { isPlacedUtility } from "@cs2/physics";
import { GuideLinesOverlay } from "../lib/guide-lines.js";
import { ThrowTechniqueIcon } from "./ThrowTechniqueIcon.js";
import { UtilityStatusBadge } from "./UtilityStatusBadge.js";
import { parseThrowTechnique } from "./throw-technique.js";
import "./throw-technique.css";
import { CloseButton, Dialog } from "../ui/index.js";
import "./utility.css";

/** Meta, Lineup-Bilder und Notiz einer Utility — geteilt von Dialog und Panel. */
function UtilityDetailBody({
  utility,
  fallbackRadarPath,
}: {
  utility: Utility;
  fallbackRadarPath: string | null;
}) {
  const technique = parseThrowTechnique(utility.technique);
  return (
    <>
      <div className="utility-detail__meta">
        <span>
          <small>START</small>
          <b>{utility.startCallout}</b>
        </span>
        <span>
          <small>ZIEL</small>
          <b>{utility.targetCallout}</b>
        </span>
        <span className="utility-detail__technique">
          <small>
            TECHNIK
            {technique ? (
              <ThrowTechniqueIcon axes={technique} size={14} />
            ) : null}
          </small>
          <b>{utility.technique}</b>
        </span>
        <span>
          <small>STATUS</small>
          <UtilityStatusBadge utility={utility} />
        </span>
      </div>
      <div className="utility-detail__frames">
        {isPlacedUtility(utility)
          ? null
          : utility.frames.map((frame) => (
              <figure
                key={frame.kind}
                style={{
                  backgroundImage: `url('${
                    frame.media?.variants[0]
                      ? `/media/${frame.media.variants[0].path}`
                      : `/${frame.legacyPath ?? fallbackRadarPath ?? ""}`
                  }')`,
                }}
              >
                <GuideLinesOverlay guides={frame.media?.guides ?? []} />
                <figcaption>
                  <b>{frame.kind}</b>
                  <span>{frame.label}</span>
                </figcaption>
              </figure>
            ))}
      </div>
      <p className="utility-detail__note">{utility.sourceNote}</p>
    </>
  );
}

export function UtilityDetailDialog({
  utility,
  fallbackRadarPath,
  onClose,
  actions,
}: {
  utility: Utility;
  fallbackRadarPath: string | null;
  onClose: () => void;
  actions?: ReactNode;
}) {
  return (
    <Dialog
      open
      onClose={onClose}
      title={utility.name}
      contentClassName="utility-detail"
    >
      <CloseButton className="ui-dialog-close-corner" onClick={onClose} />
      <p>UTILITY DETAIL</p>
      <h2>{utility.name}</h2>
      <UtilityDetailBody
        utility={utility}
        fallbackRadarPath={fallbackRadarPath}
      />
      {actions ? (
        <footer className="utility-detail__actions">{actions}</footer>
      ) : null}
    </Dialog>
  );
}

/** Dieselben Details ohne Modal, dauerhaft neben dem Radar (Ingame-Modus). */
export function UtilityDetailPanel({
  utility,
  fallbackRadarPath,
  label,
}: {
  utility: Utility;
  fallbackRadarPath: string | null;
  label: string;
}) {
  return (
    <section
      className="utility-detail utility-detail--panel"
      aria-label={label}
    >
      <p>UTILITY DETAIL</p>
      <h2>{utility.name}</h2>
      <UtilityDetailBody
        utility={utility}
        fallbackRadarPath={fallbackRadarPath}
      />
    </section>
  );
}
